import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoadmapTimeline = ({ viewMode, selectedFilter, onFeatureSelect }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const mockFeatures = [
    {
      id: 1,
      title: 'Système de Partenariat Avancé',
      status: 'completed',
      category: 'installed',
      priority: 'high',
      progress: 100,
      startDate: '2024-08-01',
      endDate: '2024-09-15',
      team: 'Backend Team',
      description: 'Système complet de gestion des partenaires avec tracking des commissions.',
      impact: 'high',
      version: 'v2.1.0',
      dependencies: []
    },
    {
      id: 2,
      title: 'Interface de Génération de Liens',
      status: 'completed',
      category: 'installed',
      priority: 'high',
      progress: 100,
      startDate: '2024-09-01',
      endDate: '2024-09-20',
      team: 'Frontend Team',
      description: 'Interface intuitive pour la création et gestion des liens de tracking.',
      impact: 'medium',
      version: 'v2.1.0',
      dependencies: [1]
    },
    {
      id: 3,
      title: 'Analytics Temps Réel',
      status: 'in_progress',
      category: 'upcoming',
      priority: 'high',
      progress: 65,
      startDate: '2024-09-10',
      endDate: '2024-10-15',
      team: 'Data Team',
      description: 'Dashboard d\'analytics en temps réel pour le suivi des performances.',
      impact: 'high',
      version: 'v2.2.0',
      dependencies: [2]
    },
    {
      id: 4,
      title: 'Optimisation Base de Données',
      status: 'planned',
      category: 'optimization',
      priority: 'medium',
      progress: 0,
      startDate: '2024-10-01',
      endDate: '2024-10-30',
      team: 'DevOps Team',
      description: 'Optimisation des requêtes et indexation pour améliorer les performances.',
      impact: 'high',
      version: 'v2.2.1',
      dependencies: []
    },
    {
      id: 5,
      title: 'Système de Notifications Push',
      status: 'planned',
      category: 'upcoming',
      priority: 'medium',
      progress: 0,
      startDate: '2024-11-01',
      endDate: '2024-11-30',
      team: 'Full Stack Team',
      description: 'Notifications push pour les partenaires et administrateurs.',
      impact: 'medium',
      version: 'v2.3.0',
      dependencies: [3]
    }
  ];

  const filteredFeatures = mockFeatures?.filter(feature => {
    if (selectedFilter === 'all') return true;
    return feature?.category === selectedFilter || feature?.status === selectedFilter;
  });

  const toggleExpanded = (featureId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(featureId)) {
      newExpanded?.delete(featureId);
    } else {
      newExpanded?.add(featureId);
    }
    setExpandedItems(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in_progress': return 'text-warning bg-warning/10';
      case 'planned': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in_progress': return 'Clock';
      case 'planned': return 'Calendar';
      default: return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (viewMode === 'timeline') {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-card-foreground">
            Timeline de Développement
          </h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Filter" size={16} />
            <span>{filteredFeatures?.length} fonctionnalités</span>
          </div>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

          <div className="space-y-6">
            {filteredFeatures?.map((feature, index) => (
              <div key={feature?.id} className="relative flex items-start space-x-4">
                {/* Timeline Node */}
                <div className={`flex-shrink-0 w-16 h-16 rounded-full border-4 border-background ${getStatusColor(feature?.status)} flex items-center justify-center z-10`}>
                  <Icon name={getStatusIcon(feature?.status)} size={24} />
                </div>

                {/* Feature Card */}
                <div className="flex-1 bg-background border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-foreground">
                          {feature?.title}
                        </h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getPriorityColor(feature?.priority)} bg-current/10`}>
                          {feature?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {feature?.description}
                      </p>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(feature?.id)}
                    >
                      <Icon 
                        name={expandedItems?.has(feature?.id) ? "ChevronUp" : "ChevronDown"} 
                        size={16} 
                      />
                    </Button>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Progression</span>
                      <span className="text-foreground font-medium">{feature?.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          feature?.status === 'completed' ? 'bg-success' :
                          feature?.status === 'in_progress' ? 'bg-warning' : 'bg-accent'
                        }`}
                        style={{ width: `${feature?.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>{formatDate(feature?.startDate)} - {formatDate(feature?.endDate)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={14} />
                      <span>{feature?.team}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Tag" size={14} />
                      <span>{feature?.version}</span>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedItems?.has(feature?.id) && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Impact Utilisateur</h5>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              feature?.impact === 'high' ? 'bg-error' :
                              feature?.impact === 'medium' ? 'bg-warning' : 'bg-success'
                            }`}></div>
                            <span className="text-sm text-muted-foreground capitalize">{feature?.impact}</span>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Dépendances</h5>
                          <div className="text-sm text-muted-foreground">
                            {feature?.dependencies?.length > 0 ? (
                              <span>{feature?.dependencies?.length} fonctionnalité(s)</span>
                            ) : (
                              <span>Aucune dépendance</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onFeatureSelect?.(feature)}
                        >
                          <Icon name="Eye" size={14} className="mr-2" />
                          Voir Détails
                        </Button>
                        {feature?.status !== 'completed' && (
                          <Button variant="ghost" size="sm">
                            <Icon name="Edit" size={14} className="mr-2" />
                            Modifier
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-card-foreground">
          Liste des Fonctionnalités
        </h3>
      </div>
      
      <div className="divide-y divide-border">
        {filteredFeatures?.map((feature) => (
          <div
            key={feature?.id}
            className="p-6 hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => onFeatureSelect?.(feature)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${getStatusColor(feature?.status)}`}>
                  <Icon name={getStatusIcon(feature?.status)} size={20} />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">{feature?.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">{feature?.progress}%</div>
                  <div className="text-xs text-muted-foreground">{feature?.team}</div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoadmapTimeline;